import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  LifeBuoy,
  CheckCircle2,
  ExternalLink,
  Paperclip,
  X as XIcon,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const FALLBACK_CATEGORIES = [
  { id: "bug", label: "Bug / Error" },
  { id: "billing", label: "Billing issue" },
  { id: "social_connection", label: "Social connection issue" },
  { id: "publishing", label: "Publishing issue" },
  { id: "ai_generation", label: "AI generation issue" },
  { id: "feature_request", label: "Feature request" },
  { id: "other", label: "Other" },
];

const FALLBACK_PRIORITIES = [
  { id: "low", label: "Low" },
  { id: "normal", label: "Normal" },
  { id: "high", label: "High" },
  { id: "urgent", label: "Urgent" },
];

// Phase 17f — attachment constraints must mirror the backend
// (see SUPPORT_ATTACHMENT_ALLOWED_MIMES / SUPPORT_ATTACHMENT_MAX_BYTES).
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";
const ATTACHMENT_MIME_ALLOWED = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);
const ATTACHMENT_EXT_ALLOWED = new Set(["png", "jpg", "jpeg", "webp"]);

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Reusable "Report a problem" dialog.
 *
 * Pass `context` to pre-fill the category/subject/message + auto-collected
 * metadata (platform, related post/publish IDs, etc). Whatever the caller
 * doesn't provide we read from `window` (page URL, user agent) so the form
 * is one-click for the customer.
 */
export default function ReportProblemDialog({
  open,
  onOpenChange,
  context = {},
  onSubmitted,
}) {
  const [meta, setMeta] = useState({
    categories: FALLBACK_CATEGORIES,
    priorities: FALLBACK_PRIORITIES,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [priority, setPriority] = useState("normal");
  const [message, setMessage] = useState("");
  // Phase 17f — real file attachment (replaces the old screenshot URL input).
  // We keep the whole file selection in local state (file object, preview
  // URL, error) so we can render preview + remove without a form re-init.
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [attachmentError, setAttachmentError] = useState(null);
  const fileInputRef = useRef(null);

  // Reset / hydrate when opened
  useEffect(() => {
    if (open) {
      setSubject(context.subject || "");
      setCategory(context.category || "other");
      setPriority(context.priority || "normal");
      setMessage(context.message || "");
      setAttachmentFile(null);
      setAttachmentPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setAttachmentError(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSubmitted(null);
    }
  }, [open, context.subject, context.category, context.priority, context.message]);

  // Revoke any lingering preview URL on unmount.
  useEffect(() => {
    return () => {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    };
    // We intentionally do NOT depend on attachmentPreview here — the
    // per-selection revocation lives inside handleFileChange / clear.
  }, []);

  // Load category labels from server when available
  useEffect(() => {
    let live = true;
    api
      .get("/support/meta")
      .then((r) => {
        if (!live) return;
        const data = r?.data || {};
        setMeta({
          categories: data.categories?.length ? data.categories : FALLBACK_CATEGORIES,
          priorities: data.priorities?.length ? data.priorities : FALLBACK_PRIORITIES,
        });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const autoMeta = useMemo(() => {
    return {
      page_url:
        context.pageUrl ||
        (typeof window !== "undefined" ? window.location.href : ""),
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : "",
      platform: context.platform || null,
      related_post_id: context.relatedPostId || null,
      related_publish_id: context.relatedPublishId || null,
    };
  }, [context.pageUrl, context.platform, context.relatedPostId, context.relatedPublishId]);

  const canSubmit =
    subject.trim().length >= 3 &&
    message.trim().length >= 5 &&
    !submitting &&
    !attachmentError;

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Type check — accept the extension too so odd browser MIME quirks still work.
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const mime = (file.type || "").toLowerCase();
    if (!ATTACHMENT_MIME_ALLOWED.has(mime) && !ATTACHMENT_EXT_ALLOWED.has(ext)) {
      setAttachmentFile(null);
      setAttachmentPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setAttachmentError("Unsupported file type. Use PNG, JPG, JPEG, or WEBP.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setAttachmentFile(null);
      setAttachmentPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setAttachmentError("File is larger than the 10 MB limit.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setAttachmentError(null);
    setAttachmentPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setAttachmentFile(file);
  };

  const clearAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setAttachmentError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let attachmentPayload = null;
      if (attachmentFile) {
        try {
          const data_base64 = await readFileAsBase64(attachmentFile);
          attachmentPayload = {
            filename: attachmentFile.name,
            content_type: attachmentFile.type || "image/png",
            data_base64,
          };
        } catch (_e) {
          toast.error("Could not read the attached file. Please try again.");
          setSubmitting(false);
          return;
        }
      }
      const res = await api.post("/support/tickets", {
        subject: subject.trim(),
        category,
        priority,
        message: message.trim(),
        attachment: attachmentPayload,
        ...autoMeta,
      });
      setSubmitted(res?.data || null);
      toast.success("Your support ticket has been submitted.");
      onSubmitted?.(res?.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not submit your ticket. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[560px] bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
        data-testid="report-problem-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <LifeBuoy className="h-4 w-4 text-[color:var(--brand-primary)]" />
            Report a problem
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div
            className="space-y-3 text-sm"
            data-testid="report-problem-success"
          >
            <div className="flex items-start gap-2 rounded-md border border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.06)] p-3">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-[color:var(--success)] shrink-0" />
              <div>
                <div className="font-semibold text-[color:var(--text-1)]">
                  Your support ticket has been submitted.
                </div>
                <div className="text-[color:var(--text-2)] mt-1">
                  Every support ticket is reviewed and receives a response within
                  {" "}<span className="font-semibold text-[color:var(--text-1)]">24 hours or less</span>.
                  We&apos;ll work to resolve your issue as quickly as possible after that.
                </div>
                <div className="text-[color:var(--text-3)] mt-1 text-xs">
                  Reference your ticket ID below if you need to follow up.
                </div>
              </div>
            </div>
            <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[color:var(--text-3)] uppercase tracking-[0.16em] text-[10px]">
                  Ticket ID
                </span>
                <span
                  className="font-mono text-[color:var(--text-1)]"
                  data-testid="report-problem-ticket-id"
                >
                  {submitted?.ticket?.ticket_id}
                </span>
              </div>
              {submitted?.email_sent === false ? (
                <div className="mt-2 text-[11px] text-[color:var(--text-3)] leading-relaxed">
                  Note: admin email notifications are not configured yet, but your ticket is
                  saved and visible to the team.
                </div>
              ) : null}
            </div>
            <div className="flex justify-between gap-2 pt-1">
              <Link
                to="/support"
                className="text-xs text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1"
                onClick={() => onOpenChange?.(false)}
                data-testid="report-problem-view-history"
              >
                View my tickets <ExternalLink className="h-3 w-3" />
              </Link>
              <Button
                onClick={() => onOpenChange?.(false)}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                data-testid="report-problem-close"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--text-2)]" data-testid="report-problem-response-guarantee">
              <Clock className="h-3.5 w-3.5 mt-0.5 text-[color:var(--brand-primary)] shrink-0" />
              <div>
                <span className="font-semibold text-[color:var(--text-1)]">Response time: Within 24 hours.</span>
                {" "}Every support ticket is reviewed and receives a response within 24 hours or less. We&apos;ll work to resolve your issue as quickly as possible after that.
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                Subject
              </label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short summary of the issue"
                className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                data-testid="report-problem-subject"
                maxLength={200}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                  Category
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger
                    className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid="report-problem-category"
                  >
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
                    {meta.categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                  Priority
                </label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger
                    className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid="report-problem-priority"
                  >
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
                    {meta.priorities.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                Describe the problem
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What happened? What did you expect? Steps to reproduce help us a lot."
                rows={5}
                className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                data-testid="report-problem-message"
                maxLength={10000}
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                Attach screenshot <span className="normal-case tracking-normal">(optional)</span>
              </label>
              <div className="mt-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleFileChange}
                  className="sr-only"
                  data-testid="report-problem-attachment-input"
                  aria-label="Attach screenshot"
                />
                {!attachmentFile ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full justify-start border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                    data-testid="report-problem-attachment-choose"
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    Choose file (PNG, JPG, JPEG, WEBP · up to 10 MB)
                  </Button>
                ) : (
                  <div
                    className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5"
                    data-testid="report-problem-attachment-preview"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <Paperclip className="h-3.5 w-3.5 text-[color:var(--brand-primary)] shrink-0" />
                      <span
                        className="font-mono text-[color:var(--text-1)] truncate flex-1"
                        data-testid="report-problem-attachment-filename"
                      >
                        {attachmentFile.name}
                      </span>
                      <span className="text-[color:var(--text-3)]" data-testid="report-problem-attachment-size">
                        {attachmentFile.size >= 1024 * 1024
                          ? `${(attachmentFile.size / (1024 * 1024)).toFixed(1)} MB`
                          : `${Math.max(1, Math.round(attachmentFile.size / 1024))} KB`}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={clearAttachment}
                        className="h-7 px-2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                        data-testid="report-problem-attachment-remove"
                        aria-label="Remove attachment"
                      >
                        <XIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    {attachmentPreview ? (
                      <div className="mt-2 flex justify-center rounded border border-[color:var(--border-subtle)] bg-[color:var(--bg-0)] max-h-[240px] overflow-hidden">
                        <img
                          src={attachmentPreview}
                          alt="Screenshot preview"
                          className="max-h-[240px] w-auto object-contain"
                          data-testid="report-problem-attachment-image"
                        />
                      </div>
                    ) : null}
                  </div>
                )}
                {attachmentError ? (
                  <div
                    className="text-[11px] text-[color:var(--accent-warm)] inline-flex items-center gap-1"
                    data-testid="report-problem-attachment-error"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" /> {attachmentError}
                  </div>
                ) : null}
              </div>
            </div>

            <div
              className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-[11px] text-[color:var(--text-3)] leading-relaxed"
              data-testid="report-problem-auto-meta"
            >
              Account and page details will be attached automatically.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="report-problem-cancel"
              >
                Cancel
              </Button>
              <Button
                disabled={!canSubmit}
                onClick={submit}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                data-testid="report-problem-submit"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <LifeBuoy className="h-4 w-4 mr-2" />
                )}
                Submit ticket
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
